import { DifficultyLevel } from '../interfaces/question.interface';
import { HopRound } from '../interfaces/fraction-hops.interface';

const MAX_TICK = 4;

/**
 * Pool of `whole × (1/den)` problems — unit-fraction hops whose product
 * lands on the 0–4 line (and usually *between* whole numbers so the
 * fraction is visible). Grade 3–4: multiplication as repeated addition.
 */
const POOL: { whole: number; den: number }[] = [
  { whole: 2, den: 2 }, { whole: 3, den: 2 }, { whole: 5, den: 2 }, { whole: 7, den: 2 },
  { whole: 2, den: 3 }, { whole: 4, den: 3 }, { whole: 5, den: 3 }, { whole: 7, den: 3 }, { whole: 8, den: 3 },
  { whole: 3, den: 4 }, { whole: 5, den: 4 }, { whole: 6, den: 4 }, { whole: 7, den: 4 }, { whole: 9, den: 4 }
].filter(p => (p.whole / p.den) <= MAX_TICK && (p.whole / p.den) >= 0.5);

function difficultyFor(whole: number, den: number): DifficultyLevel {
  const parts = whole + den;
  if (parts <= 5) return DifficultyLevel.EASY;
  if (parts <= 9) return DifficultyLevel.MEDIUM;
  return DifficultyLevel.HARD;
}

function toRound(p: { whole: number; den: number }, idx: number): HopRound {
  return {
    id: `hop-${p.whole}-${p.den}-${idx}`,
    whole: p.whole,
    num: 1,
    den: p.den,
    hop: 1 / p.den,
    product: p.whole / p.den,
    maxTick: MAX_TICK,
    difficulty: difficultyFor(p.whole, p.den)
  };
}

/**
 * Build a session's rounds: sort into an easy→hard gradient (shuffled
 * within each `whole` band) and sample `count` evenly across it, so the
 * jump-count ramps up over the turn rotation with a fresh mix.
 */
export function buildFractionHopsRounds(count: number): HopRound[] {
  const gradient = shuffleBands(POOL);
  const n = gradient.length;
  const k = Math.min(count, n);
  if (k <= 0) return [];
  if (k === 1) return [toRound(gradient[0], 0)];

  const used = new Set<number>();
  const out: HopRound[] = [];
  for (let i = 0; i < k; i++) {
    let idx = Math.round((i * (n - 1)) / (k - 1));
    while (used.has(idx)) idx = (idx + 1) % n;
    used.add(idx);
    out.push(toRound(gradient[idx], i));
  }
  return out;
}

/** Ascending by jump count (`whole`), shuffled within each band. */
function shuffleBands(pool: { whole: number; den: number }[]): { whole: number; den: number }[] {
  const bands = new Map<number, { whole: number; den: number }[]>();
  for (const p of pool) {
    const band = bands.get(p.whole) ?? [];
    band.push(p);
    bands.set(p.whole, band);
  }
  const out: { whole: number; den: number }[] = [];
  [...bands.keys()].sort((a, b) => a - b).forEach(w => {
    const band = bands.get(w)!;
    for (let i = band.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [band[i], band[j]] = [band[j], band[i]];
    }
    out.push(...band);
  });
  return out;
}
